import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Text,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
} from '@fluentui/react-components';
import {
  NavDivider,
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  NavItem,
  Hamburger,
} from '@fluentui/react-components';
import {
  Board20Filled,
  Board20Regular,
  Board24Filled,
  Board24Regular,
  Home20Filled,
  Home20Regular,
  Home24Filled,
  Home24Regular,
  TableLink20Filled,
  TableLink20Regular,
  TableLink24Filled,
  TableLink24Regular,
  PlugConnected20Filled,
  PlugConnected20Regular,
  PlugConnected24Filled,
  PlugConnected24Regular,
  PersonRegular,
  Person24Regular,
  SettingsRegular,
  Settings24Regular,
  SignOutRegular,
  WeatherSunnyRegular,
  WeatherSunny24Regular,
  WeatherMoonRegular,
  WeatherMoon24Regular,
  PersonAddRegular,
  PersonAdd24Regular,
  PeopleTeam20Regular,
  PeopleTeam24Regular,
  Share20Regular,
  Share24Regular,
  BugRegular,
  WarningRegular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useErrorReporting } from '../contexts/ErrorReportingContext';
import { canRaiseTestException, getAdminNavigation, getSidebarSelectedValue } from './sidebarNavigation';

export type SidebarMode = 'open' | 'collapsed' | 'hidden';

const HomeIcon = bundleIcon(Home20Filled, Home20Regular);
const HomeIconLg = bundleIcon(Home24Filled, Home24Regular);
const DashboardIcon = bundleIcon(Board20Filled, Board20Regular);
const DashboardIconLg = bundleIcon(Board24Filled, Board24Regular);
const DataModelIcon = bundleIcon(TableLink20Filled, TableLink20Regular);
const DataModelIconLg = bundleIcon(TableLink24Filled, TableLink24Regular);
const DataSourceIcon = bundleIcon(PlugConnected20Filled, PlugConnected20Regular);
const DataSourceIconLg = bundleIcon(PlugConnected24Filled, PlugConnected24Regular);

const AdminTestException: React.FC = () => {
  throw new Error('Administrator-triggered test exception');
};

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    display: 'flex',
    height: '100%',
  },
  navOpen: {
    minWidth: '200px',
  },
  navCollapsed: {
    minWidth: '56px',
    maxWidth: '56px',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '8px',
    paddingTop: '13px',
  },
  drawerBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  spacer: {
    flex: '1 1 auto',
  },
  accountInfo: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0.25rem 0',
  },
});

interface SidebarProps {
  mode: SidebarMode;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mode, onToggle }) => {
  const {
    user, isAuthenticated, isAdmin, isImpersonating, canImpersonate,
    canManageErrorReports, canManageSignInAccess, logout,
  } = useAuth();
  const [raiseTestException, setRaiseTestException] = React.useState(false);
  const { reportError } = useErrorReporting();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  if (mode === 'hidden') return null;

  const isCollapsed = mode === 'collapsed';
  const adminNavigation = getAdminNavigation(canImpersonate, canManageErrorReports, canManageSignInAccess);

  if (raiseTestException) return <AdminTestException />;

  return (
    <div className={styles.root}>
      <NavDrawer
        open
        type="inline"
        selectedValue={getSidebarSelectedValue(location.pathname)}
        className={isCollapsed ? styles.navCollapsed : styles.navOpen}
      >
        <NavDrawerHeader className={styles.drawerHeader}>
            <Hamburger onClick={onToggle} as="a" />
            {!isCollapsed && 'Analytics Dashboard'}
        </NavDrawerHeader>

        <NavDrawerBody className={styles.drawerBody}>
          <Tooltip content="Home" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
            <NavItem icon={isCollapsed ? <HomeIconLg /> : <HomeIcon />} value="home" onClick={() => navigate('/overview')}>
              {!isCollapsed && 'Home'}
            </NavItem>
          </Tooltip>

          <Tooltip content="Dashboards" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
            <NavItem icon={isCollapsed ? <DashboardIconLg /> : <DashboardIcon />} value="dashboards" onClick={() => navigate('/dashboards')}>
              {!isCollapsed && 'Dashboards'}
            </NavItem>
          </Tooltip>

          <Tooltip content="Shared Dashboards" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
            <NavItem icon={isCollapsed ? <Share24Regular /> : <Share20Regular />} value="shared-dashboards" onClick={() => navigate('/shared-dashboards')}>
              {!isCollapsed && 'Shared Dashboards'}
            </NavItem>
          </Tooltip>

          <Tooltip content="Data Models" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
            <NavItem icon={isCollapsed ? <DataModelIconLg /> : <DataModelIcon />} value="data-models" onClick={() => navigate('/data-models')}>
              {!isCollapsed && 'Data Models'}
            </NavItem>
          </Tooltip>

          <Tooltip content="Data Sources" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
            <NavItem icon={isCollapsed ? <DataSourceIconLg /> : <DataSourceIcon />} value="data-sources" onClick={() => navigate('/data-sources')}>
              {!isCollapsed && 'Data Sources'}
            </NavItem>
          </Tooltip>

          {adminNavigation.length > 0 && (
            <Tooltip content="Admin" relationship="label" positioning="after" visible={isCollapsed ? undefined : false}>
              <NavItem
                icon={isCollapsed ? <PeopleTeam24Regular /> : <PeopleTeam20Regular />}
                value="admin"
                onClick={() => navigate(adminNavigation[0].path)}
              >
                {!isCollapsed && 'Admin'}
              </NavItem>
            </Tooltip>
          )}

          <div className={styles.spacer} />

          <NavDivider />

          <NavItem
            icon={isDarkMode ? (isCollapsed ? <WeatherSunny24Regular /> : <WeatherSunnyRegular />) : (isCollapsed ? <WeatherMoon24Regular /> : <WeatherMoonRegular />)}
            value="theme-toggle"
            onClick={toggleTheme}
          >
            {!isCollapsed && (isDarkMode ? 'Light Mode' : 'Dark Mode')}
          </NavItem>

          <NavItem icon={isCollapsed ? <Settings24Regular /> : <SettingsRegular />} value="settings" onClick={() => navigate('/settings')}>
            {!isCollapsed && 'Settings'}
          </NavItem>

          {isAuthenticated ? (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <NavItem icon={isCollapsed ? <Person24Regular /> : <PersonRegular />} value="account">
                  {!isCollapsed && (user?.name || 'Account')}
                </NavItem>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem>
                    <div className={styles.accountInfo}>
                      <Text weight="semibold">{user?.name}</Text>
                      <Text size={200}>{user?.email}</Text>
                    </div>
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem icon={<BugRegular />} onClick={() => reportError()}>
                    Report a problem
                  </MenuItem>
                  {canRaiseTestException(isAdmin, isImpersonating) && (
                    <MenuItem icon={<WarningRegular />} onClick={() => setRaiseTestException(true)}>
                      Test error boundary
                    </MenuItem>
                  )}
                  <MenuItem icon={<SignOutRegular />} onClick={logout}>
                    Sign Out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          ) : (
            <NavItem icon={isCollapsed ? <PersonAdd24Regular /> : <PersonAddRegular />} value="sign-in" onClick={() => navigate('/login')}>
              {!isCollapsed && 'Sign In'}
            </NavItem>
          )}
        </NavDrawerBody>
      </NavDrawer>
    </div>
  );
};

export default Sidebar;